// Client-side estimates for the composer. The preview endpoint returns the
// real figures (segments per recipient after names are filled in).

export const MAX_MESSAGE_LENGTH = 918;
export const PLACEHOLDERS = ['first_name', 'last_name'];

// GSM 03.38 basic character set.
const GSM_BASIC = new Set(
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà'
);
// Extension table: each of these counts as two characters in GSM-7.
const GSM_EXTENDED = new Set('^{}\\[]~|€\f');

const CURLY = { '‘': "'", '’': "'", '“': '"', '”': '"' };

const PLACEHOLDER_RE = /\{\{\s*([^{}]*?)\s*\}\}/g;

// A typical name length, as the API docs suggest, for the live counter.
const SAMPLE = { first_name: 'Margaret', last_name: 'Wanjiku' };

export const unknownPlaceholders = (template) =>
  [...template.matchAll(PLACEHOLDER_RE)]
    .map((match) => match[1])
    .filter((name) => !PLACEHOLDERS.includes(name));

const fillSample = (template) =>
  template.replace(PLACEHOLDER_RE, (whole, name) => SAMPLE[name] ?? whole);

export const nonGsmCharacters = (text) => [
  ...new Set(
    [...text].filter((ch) => !GSM_BASIC.has(ch) && !GSM_EXTENDED.has(ch))
  ),
];

export const hasCurlyQuotes = (text) => /[‘’“”]/.test(text);

export const straightenQuotes = (text) =>
  text.replace(/[‘’“”]/g, (ch) => CURLY[ch]);

// Estimate the SMS parts for one recipient with a typical name.
export const estimateParts = (template) => {
  const text = fillSample(template.trim());
  if (!text) return { encoding: 'GSM-7', length: 0, parts: 0 };

  if (nonGsmCharacters(text).length === 0) {
    const length = [...text].reduce(
      (sum, ch) => sum + (GSM_EXTENDED.has(ch) ? 2 : 1),
      0
    );
    return {
      encoding: 'GSM-7',
      length,
      parts: length <= 160 ? 1 : Math.ceil(length / 153),
    };
  }
  // UCS-2 counts UTF-16 code units, so an emoji takes two.
  const { length } = text;
  return {
    encoding: 'UCS-2',
    length,
    parts: length <= 70 ? 1 : Math.ceil(length / 67),
  };
};

// Custom list: one number per line or comma-separated.
export const parsePhoneNumbers = (raw) =>
  raw
    .split(/[\n,]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);
